import { useQuery, useMutation } from 'convex/react'; // CROSS_IMPORT_JUSTIFIED: convex/react is an npm package, not a convex/ directory import
import { api } from '@convex/_generated/api';
import { useCallback } from 'react';
import { toast } from 'sonner';
import type { TRuleType, TAiReplyPlatform } from '@/@types/aiReply';
import type { Id } from '@convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: need Id type for rule IDs

export function useLearnedRules() {
  const rules = useQuery(api['aiReply/learnedRules'].list);
  const addMutation = useMutation(api['aiReply/learnedRules'].add);
  const updateMutation = useMutation(api['aiReply/learnedRules'].update);
  const toggleMutation = useMutation(api['aiReply/learnedRules'].toggle);
  const removeMutation = useMutation(api['aiReply/learnedRules'].remove);

  const addRule = useCallback(
    async (rule: string, ruleType: TRuleType, platform?: TAiReplyPlatform) => {
      try {
        await addMutation({ rule, ruleType, platform });
        toast.success('Rule added');
      } catch {
        toast.error('Failed to add rule');
      }
    },
    [addMutation]
  );

  const updateRule = useCallback(
    async (ruleId: Id<'aiLearnedRules'>, updates: { rule?: string; ruleType?: TRuleType }) => {
      try {
        await updateMutation({ ruleId, ...updates });
        toast.success('Rule updated');
      } catch {
        toast.error('Failed to update rule');
      }
    },
    [updateMutation]
  );

  const toggleRule = useCallback(
    async (ruleId: Id<'aiLearnedRules'>, isActive: boolean) => {
      try {
        await toggleMutation({ ruleId, isActive });
      } catch {
        toast.error('Failed to toggle rule');
      }
    },
    [toggleMutation]
  );

  const deleteRule = useCallback(
    async (ruleId: Id<'aiLearnedRules'>) => {
      try {
        await removeMutation({ ruleId });
        toast.success('Rule deleted');
      } catch {
        toast.error('Failed to delete rule');
      }
    },
    [removeMutation]
  );

  return {
    rules: rules ?? [],
    isLoading: rules === undefined,
    addRule,
    updateRule,
    toggleRule,
    deleteRule,
  };
}
